
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { SIDEBAR_MENU, SidebarMenuItem } from 'src/app/layouts/main-layout/components/sidebar/config/sidebar-menu.config';

interface ErrorDefinition {
  title: string;
  message: string;
}

@Component({
  selector: 'app-error-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './error-page.component.html',
  styleUrl: './error-page.component.scss'
})
export class ErrorPageComponent implements OnInit, OnDestroy {
  code = '404';
  title = 'Page not found';
  message = 'The requested route does not exist or has not been developed yet.';
  /** true quando l'URL è una voce del menu laterale la cui pagina non esiste ancora. */
  comingSoon = false;
  logoUrl: string | null = null;
  private subscription = new Subscription();

  private readonly errorMap: Record<string, ErrorDefinition> = {
    '401': {
      title: 'Unauthorized',
      message: 'You do not have permission to access this resource.'
    },
    '403': {
      title: 'Access denied',
      message: 'You are not authorized to view this page.'
    },
    '404': {
      title: 'Page not found',
      message: 'The requested route does not exist or has not been developed yet.'
    },
    '500': {
      title: 'Server error',
      message: 'An internal error occurred. Please try again later.'
    },
    '503': {
      title: 'Service unavailable',
      message: 'The service is temporarily unavailable.'
    }
  };

  private readonly comingSoonDefinition: ErrorDefinition = {
    title: 'Soon available',
    message: 'This section is under development and will be available soon.'
  };

  /** Percorsi delle voci del menu laterale: se una non ha ancora una rotta, finisce qui. */
  private readonly sidebarLinks = new Set(
    SIDEBAR_MENU.flatMap(section => this.collectLinks(section.items))
  );

  constructor(
    private route: ActivatedRoute,
    private mainLayoutService: MainLayoutService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  onLogoError(): void {
    this.logoUrl = null;
  }

  private preloadLogo(url: string): void {
    if (url === this.logoUrl) return;
    const img = new Image();
    img.onload = () => this.ngZone.run(() => { this.logoUrl = url; this.cdr.detectChanges(); });
    img.onerror = () => { };
    img.src = url;
  }

  ngOnInit(): void {
    const currentUrl = this.mainLayoutService.getLogoUrl();
    if (currentUrl) this.preloadLogo(currentUrl);

    this.subscription.add(
      this.mainLayoutService.logoUrl$.subscribe(url => {
        if (url) this.preloadLogo(url);
      })
    );

    // La rotta '**' riusa il componente passando da un URL inesistente all'altro:
    // il contenuto va ricalcolato a ogni cambio di URL, non solo all'avvio.
    this.subscription.add(
      this.route.url.subscribe(() => this.resolveContent())
    );
  }

  private resolveContent(): void {
    const codeFromParam = this.route.snapshot.paramMap.get('code');
    const codeFromData = this.route.snapshot.data?.['code'];
    const resolvedCode = (codeFromParam || codeFromData || '404').toString();

    const currentPath = '/' + this.route.snapshot.pathFromRoot
      .flatMap(snapshot => snapshot.url)
      .map(segment => segment.path)
      .join('/');
    this.comingSoon = resolvedCode === '404' && this.sidebarLinks.has(currentPath);

    const definition = this.comingSoon
      ? this.comingSoonDefinition
      : this.errorMap[resolvedCode] ?? this.errorMap['404'];

    this.code = resolvedCode;
    this.title = definition.title;
    this.message = definition.message;
  }

  private collectLinks(items: SidebarMenuItem[]): string[] {
    return items.flatMap(item => [
      ...(item.routerLink ? [item.routerLink] : []),
      ...this.collectLinks(item.children ?? [])
    ]);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
